/**
 * MỌI CHỮ TRÊN TRANG CHỦ NẰM Ở FILE NÀY.
 *
 * Muốn đổi tiêu đề, mô tả, tên nút, link — sửa ở đây rồi build lại
 * (`git push` → trên VPS: `git pull && docker compose up -d --build`).
 * Không cần đụng vào bất kỳ file .tsx nào.
 *
 * Điều khoản dịch vụ và chính sách bảo mật nằm cạnh đây: `terms.md`, `privacy.md`.
 */

import type { IconName } from '../components/icons';

export const landing = {
  /** Tên hiện trên thanh trên cùng và tiêu đề hero. */
  brand: 'GenSolva',

  /**
   * Dòng lớn ngay dưới tên.
   *
   * Phải làm HAI việc cùng lúc, và bản cũ ("The Discord bot that answers so your
   * team doesn't have to") chỉ làm được việc thứ nhất:
   *   1. nói bot này là loại gì — người vừa vào chưa biết đây là bot hỗ trợ;
   *   2. mang theo điểm khác biệt, vì khối `differentiators` ngay bên dưới sẽ
   *      khai triển đúng ý này, và tiêu đề không được nói một đằng còn phần
   *      chứng minh nói một nẻo.
   *
   * "Drafts" chứ không phải "writes": bot chỉ ĐỀ XUẤT, người duyệt. Cả trang
   * bán hàng bằng lời hứa "không bịa, không tự quyết" nên chính dòng tiêu đề
   * không được phép nói quá — đây là chỗ dễ mất uy tín nhất.
   */
  tagline:
    'The Discord bot that answers your members — and drafts its own FAQ from their conversations.',

  /**
   * Đoạn giới thiệu — câu chuyện vì sao bot ra đời.
   * Mỗi phần tử mảng là một đoạn văn (<p>) riêng.
   *
   * CỐ Ý CHỈ HAI ĐOẠN. Bản cũ có bốn, và ba đoạn cuối đều nói đúng một ý ("thay
   * được mấy con bot khác") — ý đó giờ đã là tiêu đề của lưới tính năng bên
   * dưới. Giữ cả bốn thì thứ đáng đọc nhất của cả trang (khối `differentiators`)
   * bị đẩy xuống dưới bốn đoạn văn mà người đọc đã lướt qua.
   */
  intro: [
    'GenSolva was born from a real, everyday problem: community management teams ' +
      'drowning in the same support questions, answered by hand, dozens of times a day. ' +
      'It answers those questions instantly from an admin-approved FAQ — so your staff ' +
      'can spend their time on the conversations that actually need a human.',
    'From there it grew into the rest of the job: welcoming and verifying new members, ' +
      'support tickets, XP that rewards helping, moderation and automod, raid protection, ' +
      'temporary voice rooms. One bot and one dashboard instead of five.',
  ],

  /**
   * KHỐI KHÁC BIỆT — phần quan trọng nhất của cả trang.
   *
   * Đóng khung riêng, đặt TRƯỚC lưới 12 thẻ tính năng, và hiện lại nguyên vẹn ở
   * đầu trang /features. Lý do đặt trước: lưới 12 thẻ là thứ mọi bot đều có một
   * phiên bản na ná — người đọc lướt qua nó và kết luận "lại một con bot nữa".
   * Ba điều dưới đây là thứ họ không tìm được ở chỗ khác, nên chúng phải được
   * đọc trước, không phải sau.
   *
   * CỐ Ý CHỈ CÓ BA. Bôi đậm bảy thứ là không bôi đậm thứ gì. Ba thứ này được
   * chọn theo một tiêu chí duy nhất: bot khác KHÔNG làm được, chứ không phải
   * "bot mình làm tốt hơn".
   *
   * Cũng cố ý KHÔNG lấy "dùng AI/embedding thay vì keyword" làm tiêu đề. Năm
   * 2026 mọi sản phẩm đều dán chữ AI lên trang chủ, nên câu đó không còn phân
   * biệt được ai với ai — nó chỉ là bằng chứng kỹ thuật cho lời hứa "không bịa",
   * nên nó nằm trong phần thân của mục 02, không nằm ở tiêu đề.
   */
  differentiators: {
    eyebrow: 'What makes it different',
    title: 'Three things no other Discord bot does',
    strapline:
      'Almost everything further down this page, some other bot also does — often for years ' +
      'and often for free. These three are the reason GenSolva was built at all.',
    items: [
      {
        title: 'It learns your product from your own community',
        body:
          'The bot reads the support channels you choose, finds the moments where one member ' +
          'actually answered another, scores those pairs, and proposes the good ones as new ' +
          'FAQ entries. Tickets your staff already closed feed the same queue. You approve or ' +
          'reject — about twenty minutes a week — and from then on the bot answers that ' +
          'question itself.',
        contrast:
          'Every other bot answers from a FAQ a human has to write and keep current. That FAQ ' +
          'is accurate the week you set it up and stale a month later. This one shows up every ' +
          'week with a list of what to add, drafted from conversations that already happened.',
      },
      {
        title: 'It would rather say “I don’t know” than guess',
        body:
          'Answers come only from entries your admins approved — the AI understands the ' +
          'question and phrases the reply, it never supplies the facts. Matching is semantic ' +
          'and cross-lingual, so a question asked in Vietnamese finds the entry written in ' +
          'English, which keyword search cannot do. If nothing matches well enough, the bot ' +
          'says it has no information and points to a ticket. If the question is too vague, it ' +
          'asks a follow-up instead of guessing.',
        contrast:
          'A general-purpose AI bot dropped into your server will invent a refund policy with ' +
          'total confidence, and your staff finds out when a member quotes it back at them. One ' +
          'wrong confident answer costs more trust than a hundred honest “I don’t know”s.',
      },
      {
        title: 'It measures what nobody else measures',
        body:
          'Which topics your community actually asks about, ranked. Every question the bot ' +
          'could not answer, grouped by theme, in a weekly report. How each support staffer and ' +
          'collab is doing: workload, response speed, accuracy. And which members genuinely ' +
          'help — an accepted answer is worth far more XP than a message, so being useful ' +
          'outranks being loud.',
        contrast:
          'Other bots can tell you who sent the most messages. None of them can tell you what ' +
          'your community needed and did not get — which is the only number that changes what ' +
          'you do next week.',
      },
    ],
    /**
     * Vòng lặp của mục 01, vẽ thành bốn bước. Đây là cơ chế khó tin nhất trong
     * ba mục ("bot tự viết FAQ?"), nên nó cần được chỉ ra từng bước thay vì chỉ
     * được khẳng định — và bước cuối phải nói rõ có người duyệt.
     */
    loop: {
      title: 'How the self-improving part actually works',
      steps: [
        {
          label: 'Listen',
          detail:
            'in the community channels you pick, and in the tickets your staff already closed',
        },
        {
          label: 'Pair',
          detail:
            'find where one member’s reply actually resolved another’s question, and score how ' +
            'good it was',
        },
        {
          label: 'Propose',
          detail: 'the strong pairs arrive in an approval queue, already rewritten as FAQ entries',
        },
        {
          label: 'Answer',
          detail: 'one click to approve, and the bot handles that question from then on',
        },
      ],
      footnote:
        'Nothing reaches your members without a human approving it. The bot proposes; your team ' +
        'decides.',
    },
  },

  /**
   * Khối giá. Hiện trên trang chủ và trang Features, dùng CHUNG một nội dung để
   * hai chỗ không bao giờ nói hai điều khác nhau về tiền.
   *
   * Nói thẳng "hiện miễn phí, sau có thể thu phí" ngay từ đầu là có chủ đích: một
   * cộng đồng phát hiện điều đó vào ngày bị chặn tính năng sẽ coi đó là bị lừa,
   * kể cả khi điều khoản đã ghi từ trước.
   */
  pricing: {
    badge: 'Free to use',
    title: 'What it costs',
    body: [
      'GenSolva is free to use right now — nothing on this page is behind a paywall.',
      'As the bot grows, some advanced features may become paid. If that happens, we ' +
        'announce it in your server in advance and nothing gets switched off overnight. ' +
        'Your community’s data stays yours either way.',
      'One cost is never ours to charge: the AI provider key your server registers. You pay ' +
        'that provider directly — and the recommended default has a free tier that most ' +
        'communities never exceed.',
    ],
  },

  /**
   * DẢI SỐ LIỆU — bằng chứng cho khối khác biệt ngay phía trên.
   *
   * Số do `/api/public/stats` cấp, cộng dồn từ mọi server, không phải chữ khai ở
   * đây. `{...}` là chỗ điền; xem `components/public/PublicStats.tsx`.
   *
   * Bốn ô được chọn theo một luật: **chỉ số ĐẾM, không chỉ số TỈ LỆ.** Tỉ lệ trả
   * lời được và tỉ lệ hài lòng đều tính được từ cùng bộ dữ liệu nhưng cố ý không
   * có ở đây — lý do ở `docs/11-so-lieu-cong-khai.md` mục 2.4. Nhóm FAQ (tổng số
   * mục, số đã duyệt/từ chối) cũng đã bỏ: số còn quá nhỏ để đứng cạnh 1.430.
   *
   * Ô thứ ba là ô đáng giá nhất và cũng dễ bị hiểu ngược nhất, nên nó luôn hiện
   * kèm mẫu số: "bot im lặng N lần" mà không nói "so với M lần nó nói" thì nghe
   * như bot hỏng.
   */
  stats: {
    eyebrow: 'Measured, not claimed',
    /** `{since}` = tháng/năm của sự kiện sớm nhất đọc được. */
    caption: 'Across every server running GenSolva, since {since}.',
    tiles: {
      pairs: {
        label: 'Question–answer pairs matched',
        /** `{live}`, `{accepted}`, `{ticket}` */
        note: '{live} from live conversations · {accepted} from answers a member marked as the one that helped · {ticket} from closed tickets',
      },
      questions: {
        label: 'Questions it could not answer, and logged',
        note: 'Not swept under the rug. Each one is material for the next FAQ entry.',
      },
      yielded: {
        label: 'Times it went quiet because a member answered first',
        /** `{answered}` = số lượt bot đã trả lời, làm mẫu số. */
        note: 'Against {answered} times it did answer — it defers to your community more often than it speaks.',
      },
      latency: {
        label: 'Median time to answer',
        note: 'Measured from the moment the question is posted.',
      },
    },
    languages: {
      label: 'Answered in',
      /** Mã ngôn ngữ không có ở đây thì hiện nguyên mã, không giấu đi. */
      names: { vi: 'Tiếng Việt', en: 'English', id: 'Bahasa Indonesia', th: 'ไทย' } as Record<
        string,
        string
      >,
    },
  },

  /**
   * Tiêu đề của lưới thẻ bên dưới khối khác biệt. Có tiêu đề là để nói thẳng
   * rằng phần còn lại là "và đây là mọi thứ khác nó cũng làm" — chứ không phải
   * một lưới tính năng trôi nổi ngay sau ba mục vừa được nhấn mạnh.
   */
  featuresHeading: 'Everything else it does, so you can stop paying for the other bots',

  /**
   * Các thẻ tính năng ở giữa trang. Thêm/bớt phần tử tuỳ ý.
   *
   * `icon` là tên tra trong bộ `<Icon>` dùng chung với panel (`components/icons.tsx`),
   * không phải emoji: emoji vẽ khác nhau mỗi máy/hệ điều hành và không đổi theo
   * dark mode, đúng lý do panel đã bỏ chúng — xem chú thích đầu file đó.
   */
  features: [
    {
      icon: 'chat-dots' satisfies IconName,
      title: 'FAQ answers, instantly',
      body: 'Answers member questions from a curated FAQ with AI-assisted understanding — and says "I don’t know" instead of making things up.',
    },
    {
      icon: 'globe' satisfies IconName,
      title: '4 languages',
      body: 'Detects each member’s language and replies in it: Tiếng Việt, English, Bahasa Indonesia, ไทย.',
    },
    {
      icon: 'star' satisfies IconName,
      title: 'XP that rewards helping',
      body: 'Points come from accepted answers and real activity, not spam. Anti-abuse layers catch alt accounts and farming pairs.',
    },
    {
      icon: 'ticket' satisfies IconName,
      title: 'Built-in ticket system',
      body: 'A "Create ticket" button with a custom form, private channels, claiming, and a transcript saved before the channel is deleted — all configured from the dashboard.',
    },
    {
      icon: 'id-badge' satisfies IconName,
      title: 'Welcome & autorole',
      body: 'Greets new members with a custom message and image card, DMs them the rules, hands out starter roles, and skips brand-new accounts during raids.',
    },
    {
      icon: 'gift' satisfies IconName,
      title: 'Rewards & raffle',
      body: 'Weekly and monthly leaderboards, personal milestones, and a provably fair raffle — every draw can be independently verified.',
    },
    {
      icon: 'shield-check' satisfies IconName,
      title: 'Moderation & automod',
      body: 'Ban, kick, timeout, warn, purge and channel locks — plus automatic spam and banned-word handling, with every action kept in a searchable infraction history and an edit/delete message log.',
    },
    {
      icon: 'check-circle' satisfies IconName,
      title: 'Verification gate & raid guard',
      body: 'New members click one button before they can talk. If a join wave looks like a raid, the bot locks your invite links and pings your mods instead of mass-banning real people.',
    },
    {
      icon: 'users-three' satisfies IconName,
      title: 'Temporary voice rooms',
      body: 'Join one channel and the bot creates a private voice room you own — rename it, set a limit, lock it. It disappears by itself when the last person leaves.',
    },
    {
      icon: 'robot' satisfies IconName,
      title: 'Reaction roles & auto messages',
      body: 'Self-service role panels with buttons, sticky messages that stay at the bottom of a channel, scheduled announcements, keyword auto-replies, and scheduled cleanup of noisy channels.',
    },
    {
      icon: 'chart-line' satisfies IconName,
      title: 'Knowledge gap reports',
      body: 'Every unanswered question is logged and summarized weekly, so your team always knows what the FAQ is missing.',
    },
    {
      icon: 'gear-six' satisfies IconName,
      title: 'Full web dashboard',
      body: 'Manage FAQ, translations, members, rewards and settings from this site — protected by Discord sign-in and your server roles.',
    },
  ],

  /**
   * Thanh điều hướng dùng CHUNG cho mọi trang public (giới thiệu, tính năng,
   * lệnh, hướng dẫn, điều khoản). Thêm một mục ở đây là nó hiện trên mọi trang.
   */
  nav: [
    { label: 'Features', url: '/features' },
    { label: 'Commands', url: '/commands' },
    { label: 'Setup guide', url: '/setup' },
  ],

  /** Các nút. Đổi chữ hoặc link đều ở đây. */
  buttons: {
    addBot: {
      label: 'Add to Discord',
      // Link mời bot. Sinh lại trong Discord Developer Portal → OAuth2 → URL
      // Generator nếu muốn đổi bộ quyền.
      //
      // permissions=1409169091831 là ĐÚNG bộ quyền code đang gọi tới, không phải
      // bộ "cho rộng cho chắc". Thiếu bất kỳ quyền nào dưới đây là một tính năng
      // im lặng hỏng ở lần dùng đầu tiên:
      //   CreateInstantInvite + ManageGuild → chống raid khoá/tạo lại link mời
      //   KickMembers, BanMembers, ModerateMembers, ManageNicknames → /mod, automod
      //   ManageChannels + MoveMembers → phòng voice tạm (Join to Create), kênh ticket
      //   ManageRoles → cổng xác minh, autorole, reaction role, role theo level
      //   ManageMessages → /purge, auto purge, sticky, dọn tin trong ticket
      //   ViewAuditLog → đối chiếu người thực hiện khi ghi log kiểm duyệt
      //   ViewChannel, SendMessages, EmbedLinks, AttachFiles, ReadMessageHistory,
      //   AddReactions, UseExternalEmojis, CreatePublicThreads,
      //   SendMessagesInThreads → mọi thứ còn lại: trả lời, log, thiệp chào mừng
      url:
        'https://discord.com/oauth2/authorize?client_id=1531878629234184204' +
        '&scope=bot+applications.commands&permissions=1409169091831',
    },
    /**
     * CHỈ có nhãn, cố ý không có `url`.
     *
     * Link đăng nhập phải mang theo trang đích, mà trang đích thì phụ thuộc vào
     * chỗ người dùng đang đứng — nên nó do `lib/loginHref.ts` sinh ra tại chỗ.
     * Để lại một `url: '/api/auth/login'` ở đây là để lại một hằng số trông như
     * nguồn sự thật nhưng không chỗ nào đọc: người sửa nó sẽ tin mình vừa đổi
     * được luồng đăng nhập.
     */
    login: { label: 'Sign in with Discord' },
    /**
     * Thay chỗ nút Đăng nhập khi phiên đã có — xem `PublicShell`.
     *
     * CHỈ có nhãn, cùng lý do với `login` ngay trên: đích thật (`/` hay
     * `/overview`) phụ thuộc vào server đang chọn, nên `PublicShell` tự tính
     * lấy `panelHref` tại chỗ chứ không đọc từ đây.
     */
    openPanel: { label: 'Open admin panel' },
    terms: { label: 'Terms of Service', url: '/terms' },
    privacy: { label: 'Privacy Policy', url: '/privacy' },
    setup: { label: 'Read the setup guide', url: '/setup' },
    features: { label: 'See everything it does', url: '/features' },
    commands: { label: 'Browse all commands', url: '/commands' },
  },

  /** Dòng chân trang. */
  footer: '© 2026 GenSolva. Not affiliated with Discord Inc.',
} as const;
